/**
 * Tampering and hooking of capability APIs and intrinsics, live and from the
 * end-of-run global diff (ADR-0012, docs/sandbox.md §6). Owned by Phase 4
 * lane B.
 *
 * Live evidence is `globals` events tagged `tamper` (an assignment or
 * `defineProperty` on a guarded API, a DOM prototype, or an intrinsic
 * prototype); the diff adds what live detection can't see (plain
 * assignments to intrinsic prototypes, `delete`s). What a path means comes
 * from `tamperGroup` (core/environments/browser/tamper.ts).
 */
import { type TamperGroup, tamperGroup, tamperPath } from "../../environments/browser/tamper";
import type { AuditEvent, GlobalChange, Severity } from "../../events";
import type { AnalysisInput, Rule, RuleEscalation, RuleFinding } from "../types";

interface Tampered {
  path: string;
  group: TamperGroup;
  /** Evidence `seq`s, in log order. */
  seqs: number[];
  /** The diff's verdict, if the path changed by the end of the run. */
  change?: GlobalChange["change"];
}

const isTamperEvent = (e: AuditEvent) => e.category === "globals" && e.tags.includes("tamper");

/** Every tampered path with its group, live events, and end-of-run change, in first-seen order. */
function tampered(input: AnalysisInput): Tampered[] {
  const byPath = new Map<string, Tampered>();
  const entry = (path: string): Tampered | null => {
    const existing = byPath.get(path);
    if (existing) return existing;
    const group = tamperGroup(path);
    if (!group) return null;
    const created: Tampered = { path, group, seqs: [] };
    byPath.set(path, created);
    return created;
  };
  for (const e of input.events) if (isTamperEvent(e)) entry(e.target)?.seqs.push(e.seq);
  for (const change of input.globalDiff) {
    const item = entry(tamperPath(change.path));
    if (item) item.change = change.change;
  }
  return [...byPath.values()];
}

const VERB: Record<GlobalChange["change"], string> = {
  added: "Added",
  deleted: "Deleted",
  replaced: "Hooked",
  redefined: "Redefined",
};

const HOOK_SEVERITY: Partial<Record<TamperGroup, Severity>> = {
  network: "critical",
  events: "critical",
  codegen: "suspicious",
  wallet: "critical",
};

const escalate = (seqs: number[], severity: Severity): RuleEscalation[] =>
  seqs.map((seq) => ({ seq, severity }));

/** Replacing a network, event, or dynamic-code API: the script sees (or rewrites) what the page does. */
const hooking: Rule = {
  id: "tamper.hooking",
  explanation:
    "The script replaced a browser API the page itself relies on. Wrapping fetch, XMLHttpRequest, addEventListener, or a crypto wallet's request method lets it read and rewrite the page's own requests, events, and transactions (a man-in-the-middle inside the page).",
  apply(input) {
    const findings: RuleFinding[] = [];
    const escalations: RuleEscalation[] = [];
    for (const item of tampered(input)) {
      const severity = HOOK_SEVERITY[item.group];
      // A new member on a DOM prototype isn't a hook of anything.
      if (!severity || item.change === "added") continue;
      findings.push({
        severity,
        title: `${VERB[item.change ?? "replaced"]} ${item.path}`,
        evidence: item.seqs,
        target: item.path,
        tags: ["tamper", item.group],
      });
      escalations.push(...escalate(item.seqs, severity));
    }
    return { findings, escalations };
  },
};

const list = (paths: string[]) =>
  paths.length <= 3
    ? paths.join(", ")
    : `${paths.slice(0, 3).join(", ")}, and ${paths.length - 3} more`;

/** Overwriting what analysis tools look through: toString, descriptors, call/apply, JSON. */
const instrumentation: Rule = {
  id: "tamper.instrumentation",
  explanation:
    "The script replaced built-ins that analysis and monitoring tools depend on (Function.prototype.toString, property descriptors, call/apply, JSON). Code does this to hide its hooks or to blind a sandbox.",
  apply(input) {
    const items = tampered(input).filter((t) => t.group === "instrumentation");
    const first = items[0];
    if (!first) return {};
    const seqs = items.flatMap((t) => t.seqs);
    return {
      findings: [
        {
          severity: "suspicious",
          title: `Tampered with ${list(items.map((t) => t.path))}`,
          evidence: seqs,
          target: first.path,
          tags: ["tamper", "instrumentation"],
        },
      ],
      escalations: escalate(seqs, "suspicious"),
    };
  },
};

/** New or replaced members on built-in prototypes change every object on the page. */
const pollution: Rule = {
  id: "tamper.prototype-pollution",
  explanation:
    "The script added or replaced members of built-in prototypes such as Object.prototype, which changes the behavior of every object on the page. Prototype pollution can bypass the page's own checks.",
  apply(input) {
    const items = tampered(input).filter((t) => t.group === "intrinsics");
    const first = items[0];
    if (!first) return {};
    const seqs = items.flatMap((t) => t.seqs);
    return {
      findings: [
        {
          severity: "suspicious",
          title: `Polluted built-in prototypes: ${list(items.map((t) => t.path))}`,
          evidence: seqs,
          target: first.path,
          tags: ["tamper", "prototype-pollution"],
        },
      ],
      escalations: escalate(seqs, "suspicious"),
    };
  },
};

export const TAMPER_RULES: readonly Rule[] = [hooking, instrumentation, pollution];
